/**
 * STANDING ARRANGEMENTS on a home: what the household agreed to, priced from the published card,
 * and the form that sets one up.
 *
 * NO PRICE IS TYPED HERE. CLAUDE.md seam rule 1 and binding rule 3. The form derives the card line
 * from the home's shape and the cadence, exactly as the server's `schedule/pricing.ts` does, and
 * shows the figure read only. When the card has no row for the home, or the row it has says
 * "quoted after a walkthrough", the person chooses the line she quoted from, and only for a line
 * the card itself publishes as quoted does a dollars box appear. The server refuses a typed figure
 * against any line the card prices, so the box is not drawn for those.
 *
 * Cadence lives on the agreement rather than the series because cadence sets the price. That is
 * why the list groups by agreement and the series hangs under it, and why changing cadence is not
 * an edit but a new agreement (on the agreement's own page).
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AGREEMENT_STATE_LABELS,
  CADENCES,
  CADENCE_LABELS,
  DEEP_CLEAN_AFTER_DAYS,
  SERVICE_LINES,
  SERVICE_LINE_LABELS,
  type Cadence,
  type RateLine,
  type ServiceLine,
} from '@fk/shared';
import {
  ApiError,
  createAgreement,
  listAgreements,
  type AgreementList,
  type CleanerRow,
  type NewAgreement,
  type SeriesRow,
  type SiteRow,
} from '../api.ts';
import { offeredForAClient } from '../checks.ts';
import { Pill } from '../components/AppShell.tsx';
import { Button, Field, SelectField, TextField } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { describeLine, houseLineForHome, linesForService } from '../components/PublishedRates.tsx';
import { agreementStateTone } from '../components/StatusPills.tsx';
import { CheckField } from '../components/VisitBits.tsx';
import {
  DEFAULT_WINDOW_END,
  DEFAULT_WINDOW_START,
  centsFromDollars,
  daysBetween,
  formatDay,
  formatLocalWindow,
  isDay,
  isTimeOfDay,
  money,
  weekdayName,
} from '../format.ts';

/** The cleaner's name when the crew list has her, else the id, which is honest if not pretty. */
export function cleanerName(cleaners: readonly CleanerRow[] | null, cleanerId: string | null) {
  if (!cleanerId) return 'Nobody yet';
  return cleaners?.find((cleaner) => cleaner.id === cleanerId)?.displayName ?? cleanerId;
}

/** "Thursdays, 9:00 am to 1:00 pm, with Maria". */
export function describeSeries(series: SeriesRow, cleaners: readonly CleanerRow[] | null): string {
  const slot = `${weekdayName(series.anchorDate)}s, ${formatLocalWindow(series.windowStartLocal, series.windowEndLocal)}`;
  const who = series.cleanerId
    ? `, with ${cleanerName(cleaners, series.cleanerId)}`
    : ', nobody assigned yet';
  return `${slot}${who}`;
}

export function Arrangements({
  site,
  today,
  cleaners,
  lines,
}: {
  site: SiteRow;
  today: string;
  cleaners: readonly CleanerRow[] | null;
  lines: readonly RateLine[] | null;
}) {
  const [list, setList] = useState<AgreementList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listAgreements(site.id)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The arrangements did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [site.id, attempt]);

  return (
    <div className="mt-4 border-t border-navy/10 pt-3">
      <h4 className="mb-2 font-serif text-base">Standing arrangements</h4>

      {error ? (
        <ErrorNotice
          title="The arrangements did not load"
          message={error.message}
          onRetry={reload}
        />
      ) : list === null ? (
        <Busy what="the arrangements" />
      ) : list.agreements.length === 0 ? (
        <EmptyNotice title="Nothing agreed yet">
          <p>Set up the first standing clean below. The price comes off the published card.</p>
        </EmptyNotice>
      ) : (
        <ul className="space-y-2">
          {list.agreements.map((agreement) => {
            const series = list.series.filter((row) => row.agreementId === agreement.id);
            return (
              <li key={agreement.id}>
                <Link
                  to={`/agreements/${agreement.id}`}
                  className="block min-h-11 desk:min-h-9 rounded-lg border border-navy/15 bg-cream px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {SERVICE_LINE_LABELS[agreement.serviceLine]},{' '}
                      {CADENCE_LABELS[agreement.cadence]}
                    </span>
                    <span className="text-sm">{money(agreement.priceCents)}</span>
                    <Pill tone={agreementStateTone(agreement.state)}>
                      {AGREEMENT_STATE_LABELS[agreement.state]}
                    </Pill>
                  </div>
                  {series.length === 0 ? (
                    <p className="mt-1 text-xs text-navy/65">
                      A one-off, from {formatDay(agreement.startedOn)}.
                    </p>
                  ) : (
                    series.map((row) => (
                      <p key={row.id} className="mt-1 text-xs text-navy/65">
                        {describeSeries(row, cleaners)}
                        {row.pausedFrom ? `. Paused from ${formatDay(row.pausedFrom)}` : ''}
                        {row.endedOn ? `. Ended ${formatDay(row.endedOn)}` : ''}
                      </p>
                    ))
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {lines ? (
        <SetUpArrangement site={site} today={today} cleaners={cleaners ?? []} lines={lines} />
      ) : (
        <p className="mt-3 text-xs text-navy/65">
          The published card has not loaded, so nothing can be priced and nothing can be set up
          until it does.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Choosing the card line. Shared with the cadence change on the
 * agreement's own page, because the rule is the same rule.
 * ------------------------------------------------------------------ */

export interface LineResolution {
  /** The line the server will price from, or null when the person still has to choose. */
  readonly line: RateLine | null;
  /** True when the card fixed the line from the home's shape and nobody chose anything. */
  readonly fixed: boolean;
}

/**
 * Which line will price this agreement. A home the card has a priced row for gets that row and
 * no choice; otherwise the person's pick, defaulting to the card's own quoted row when it has one
 * (six bedrooms and up).
 *
 * The one exception is a one-time house clean. The card publishes three kinds of those, the single
 * clean for the home's shape, the deep clean and the move-out clean, and the server prices any of
 * them, so the derived row is the default and the chooser stays open for the other two.
 */
export function resolveLine(
  lines: readonly RateLine[],
  serviceLine: ServiceLine,
  cadence: Cadence,
  home: { bedrooms: number | null; bathrooms: number | null },
  chosenCode: string,
): LineResolution {
  const derived =
    serviceLine === 'house_cleaning'
      ? houseLineForHome(lines, home.bedrooms, home.bathrooms, cadence)
      : null;
  if (derived && derived.priceCents !== null && cadence !== 'one_time') {
    return { line: derived, fixed: true };
  }
  const options = linesForService(lines, serviceLine, cadence);
  const chosen = options.find((line) => line.code === chosenCode) ?? null;
  return { line: chosen ?? derived, fixed: false };
}

export function RateLineChoice({
  idPrefix,
  lines,
  serviceLine,
  cadence,
  home,
  chosenCode,
  onChoose,
  quotedDollars,
  onQuoted,
}: {
  idPrefix: string;
  lines: readonly RateLine[];
  serviceLine: ServiceLine;
  cadence: Cadence;
  home: { bedrooms: number | null; bathrooms: number | null };
  chosenCode: string;
  onChoose: (code: string) => void;
  quotedDollars: string;
  onQuoted: (dollars: string) => void;
}) {
  const resolved = resolveLine(lines, serviceLine, cadence, home, chosenCode);
  const options = useMemo(
    () => linesForService(lines, serviceLine, cadence),
    [lines, serviceLine, cadence],
  );

  if (resolved.fixed && resolved.line) {
    return (
      <Field
        id={`${idPrefix}-line`}
        label="Priced from the published card"
        hint="Read off the card for a home of this shape at this cadence. Nothing here can be typed over."
      >
        <p
          id={`${idPrefix}-line`}
          className="mt-1 rounded-lg border border-gold/40 bg-gold/15 px-3 py-2 text-sm font-medium"
        >
          {describeLine(resolved.line)}
        </p>
      </Field>
    );
  }

  const current = resolved.line?.code ?? '';
  return (
    <div>
      <SelectField
        id={`${idPrefix}-line`}
        label="Which line of the card"
        required
        value={current}
        onChange={onChoose}
        options={options.map((line) => line.code)}
        toLabel={(code) => {
          const line = options.find((row) => row.code === code);
          return line ? describeLine(line) : code;
        }}
        placeholder="Choose the line they were quoted from"
        hint={
          serviceLine !== 'house_cleaning'
            ? 'Home watch and turnover are priced by the published row, not by bedrooms.'
            : cadence === 'one_time'
              ? 'A one-time visit is the single clean for a home of this shape, the deep clean or the move-out clean. The card publishes all three; choose the one they were quoted from.'
              : 'The card has no priced row for a home of this shape, so choose the row the client was quoted from.'
        }
      />
      {resolved.line && resolved.line.priceCents === null ? (
        <TextField
          id={`${idPrefix}-quoted`}
          label="The figure quoted after the walkthrough"
          required
          inputMode="numeric"
          value={quotedDollars}
          onChange={onQuoted}
          placeholder="350.00"
          hint="The card itself says this row is quoted after a walkthrough. This is the only price ever typed into this system, and only because the card says so."
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Setting one up.
 * ------------------------------------------------------------------ */

/**
 * The screen's copy of the server's rule in `schedule/agreements.ts`. A one-time clean is never
 * asked: it is one visit at the price of the line it was booked from, so there is no first visit
 * priced from another row to say out loud. Nor is a home the card publishes no deep clean row for,
 * six bedrooms and up: there is no deep clean figure to say, and its first visit is written at the
 * figure quoted after the walkthrough like every other.
 */
function needsDeepCleanDisclosure(
  site: SiteRow,
  startedOn: string,
  cadence: Cadence,
  lines: readonly RateLine[],
  code: string | null,
): boolean {
  if (cadence === 'one_time') return false;
  if (firstVisitCents(lines, code) === null) return false;
  if (site.lastProfessionalCleanBasis === 'not_applicable') return false;
  if (site.lastProfessionalCleanBasis === 'recent_clean_known') return false;
  if (site.lastProfessionalCleanOn === null) return true;
  const gap = daysBetween(site.lastProfessionalCleanOn, startedOn);
  return gap === null || gap > DEEP_CLEAN_AFTER_DAYS;
}

/**
 * What the card prices the first visit at when it is the deep clean: the deep clean row of the same
 * home shape as a recurring house clean line, the lookup the server's `deepLineFor` makes before it
 * stamps that visit. Null for any other line, and for the 6+ bed row, which has no deep clean figure.
 */
function firstVisitCents(lines: readonly RateLine[], code: string | null): number | null {
  const [family, row] = (code ?? '').split('.');
  if (family !== 'clean' || row === undefined) return null;
  return lines.find((line) => line.code === `deep.${row}`)?.priceCents ?? null;
}

function SetUpArrangement({
  site,
  today,
  cleaners,
  lines,
}: {
  site: SiteRow;
  today: string;
  cleaners: readonly CleanerRow[];
  lines: readonly RateLine[];
}) {
  const navigate = useNavigate();
  const [serviceLine, setServiceLine] = useState<ServiceLine>('house_cleaning');
  const [cadence, setCadence] = useState<Cadence>('every_2_weeks');
  const [chosenCode, setChosenCode] = useState('');
  const [quotedDollars, setQuotedDollars] = useState('');
  const [startedOn, setStartedOn] = useState(today);
  const [anchorDate, setAnchorDate] = useState('');
  const [windowStart, setWindowStart] = useState(DEFAULT_WINDOW_START);
  const [windowEnd, setWindowEnd] = useState(DEFAULT_WINDOW_END);
  const [cleanerId, setCleanerId] = useState('');
  const [deepCleanDisclosed, setDeepCleanDisclosed] = useState(false);
  const [accessConfirmed, setAccessConfirmed] = useState(false);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Active and carrying the owners' checks mark (ops #85); the server refuses anybody else.
  const active = cleaners.filter((cleaner) => offeredForAClient(cleaner));
  const resolved = resolveLine(lines, serviceLine, cadence, site, chosenCode);
  const askDeepClean = needsDeepCleanDisclosure(
    site,
    startedOn,
    cadence,
    lines,
    resolved.line?.code ?? null,
  );
  const oneTime = cadence === 'one_time';
  // The two figures the person says out loud: the first visit as the deep clean, then every visit.
  const recurringCents = resolved.line?.priceCents ?? null;
  const deepCents =
    askDeepClean && !oneTime && recurringCents !== null
      ? firstVisitCents(lines, resolved.line?.code ?? null)
      : null;

  function problemsWith(): string[] {
    const found: string[] = [];
    if (!resolved.line) found.push('Choose the line of the card this is priced from.');
    if (resolved.line?.priceCents === null && centsFromDollars(quotedDollars) === null) {
      found.push('Type the figure that was quoted after the walkthrough, in dollars.');
    }
    if (!isDay(startedOn)) found.push('The agreement starts on a day.');
    if (!isDay(anchorDate)) {
      found.push(oneTime ? 'Pick the day of the clean.' : 'Pick the day of the first visit.');
    } else if (isDay(startedOn) && anchorDate < startedOn) {
      found.push('The first visit cannot be before the agreement starts.');
    }
    if (!isTimeOfDay(windowStart) || !isTimeOfDay(windowEnd)) {
      found.push('The arrival window needs a start and an end time.');
    } else if (windowStart >= windowEnd) {
      found.push('The arrival window has to end after it starts.');
    }
    if (askDeepClean && !deepCleanDisclosed) {
      found.push(
        'This home has had no professional clean in the last ninety days that we know of, so the first visit is priced as a deep clean. Say that to the client, then tick that it was said.',
      );
    }
    return found;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found = problemsWith();
    setProblems(found);
    if (found.length > 0 || !resolved.line) return;

    const slot = {
      windowStartLocal: windowStart,
      windowEndLocal: windowEnd,
      cleanerId: cleanerId || null,
    };
    const body: NewAgreement = {
      siteId: site.id,
      serviceLine,
      cadence,
      rateLineCode: resolved.line.code,
      quotedPriceCents: resolved.line.priceCents === null ? centsFromDollars(quotedDollars) : null,
      startedOn,
      accessConfirmed,
      deepCleanDisclosed: askDeepClean ? deepCleanDisclosed : undefined,
      ...(oneTime
        ? { oneOff: { date: anchorDate, ...slot } }
        : { series: { anchorDate, ...slot } }),
    };

    setSaving(true);
    try {
      const result = await createAgreement(body);
      navigate(`/agreements/${result.agreementId}`);
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'That arrangement was not saved.');
      setSaving(false);
    }
  }

  return (
    <details className="mt-3 rounded-lg border border-navy/15 p-3">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        Set up a standing clean
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Nothing was saved" message={failure} />
          </div>
        ) : null}
        {problems.length > 0 ? (
          <div
            className="mb-3 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-3"
            role="alert"
          >
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <SelectField
          id={`arr-${site.id}-service`}
          label="Service"
          value={serviceLine}
          onChange={(value) => {
            setServiceLine(value);
            setChosenCode('');
          }}
          options={SERVICE_LINES}
          toLabel={(value) => SERVICE_LINE_LABELS[value]}
        />
        <SelectField
          id={`arr-${site.id}-cadence`}
          label="How often"
          value={cadence}
          onChange={(value) => {
            setCadence(value);
            setChosenCode('');
          }}
          options={CADENCES}
          toLabel={(value) => CADENCE_LABELS[value]}
          hint="Cadence sets the price on the card, so changing it later ends this agreement and opens another."
        />

        <RateLineChoice
          idPrefix={`arr-${site.id}`}
          lines={lines}
          serviceLine={serviceLine}
          cadence={cadence}
          home={site}
          chosenCode={chosenCode}
          onChoose={setChosenCode}
          quotedDollars={quotedDollars}
          onQuoted={setQuotedDollars}
        />

        <TextField
          id={`arr-${site.id}-started`}
          label="Agreement starts"
          type="date"
          required
          value={startedOn}
          onChange={setStartedOn}
        />
        <TextField
          id={`arr-${site.id}-anchor`}
          label={oneTime ? 'Day of the clean' : 'First visit'}
          type="date"
          required
          value={anchorDate}
          onChange={setAnchorDate}
          hint={
            oneTime
              ? undefined
              : `Every visit after it falls on the same weekday${isDay(anchorDate) ? `, a ${weekdayName(anchorDate)}` : ''}.`
          }
        />
        <div className="grid grid-cols-2 gap-3">
          <Field id={`arr-${site.id}-start`} label="Arrives from">
            <input
              id={`arr-${site.id}-start`}
              type="time"
              value={windowStart}
              onChange={(event) => setWindowStart(event.target.value)}
              className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
            />
          </Field>
          <Field id={`arr-${site.id}-end`} label="Arrives by">
            <input
              id={`arr-${site.id}-end`}
              type="time"
              value={windowEnd}
              onChange={(event) => setWindowEnd(event.target.value)}
              className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
            />
          </Field>
        </div>
        <SelectField
          id={`arr-${site.id}-cleaner`}
          label="Whose slot it is"
          value={cleanerId}
          onChange={setCleanerId}
          options={active.map((cleaner) => cleaner.id)}
          toLabel={(id) => active.find((cleaner) => cleaner.id === id)?.displayName ?? id}
          placeholder="Nobody yet"
        />

        {askDeepClean ? (
          <CheckField
            id={`arr-${site.id}-deep`}
            label="I said the first visit is priced as a deep clean"
            checked={deepCleanDisclosed}
            onChange={setDeepCleanDisclosed}
            hint={
              deepCents !== null && recurringCents !== null
                ? `The card prices the first visit at ${money(deepCents)} as the deep clean, then ${money(recurringCents)} a visit. Said before booking and never on the invoice afterwards.`
                : 'Said before booking and never on the invoice afterwards. The card publishes the deep clean figures.'
            }
          />
        ) : null}
        <CheckField
          id={`arr-${site.id}-access`}
          label="How the cleaner gets in is settled"
          checked={accessConfirmed}
          onChange={setAccessConfirmed}
          hint="Keys held, code known, vendor registration approved where the community is gated."
        />

        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : oneTime ? 'Book the clean' : 'Set up the standing clean'}
        </Button>
      </form>
    </details>
  );
}
